import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Asset, CareRecord, Contact, Product, Todo } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import { instantDe, raisonDe, sac } from './morceaux.js';
import type { Fiche } from './morceaux.js';
import { valeurEnTexte } from './temps.js';
import { MODELE_PAR_DEFAUT, pointsDeLExamen } from './examen.js';
import type { PointExamine } from './examen.js';
import { courbeDuPoids, lignesDHistorique } from './apercu.js';
import type { LigneDHistorique, PointDePoids, VisitePassee } from './apercu.js';
import { aProposer as aProposerDuCarnet, lignesDuCarnet } from './carnet.js';
import type { LigneDuCarnet, ProduitDuCarnet } from './carnet.js';
import type { TacheDuSuivi } from './PanneauSuivi.js';

/**
 * LE DOSSIER D'UN ANIMAL, LU POUR LA CONSULTATION OUVERTE : le proprietaire,
 * les soins et les taches de l'animal, son carnet de vaccination, son
 * historique clinique et sa courbe de poids. Ce qui ne regarde que la
 * consultation en cours (lignes du jour, cloture, compte rendu) reste dans
 * `DossierDeConsultation.tsx`.
 *
 * TOUT PASSE PAR LE SDK : les rules jugent chaque lecture et chaque ecriture.
 */

/** Les libelles des systemes de l'examen, pour nommer une anomalie dans l'historique. */
const SYSTEMES_DE_L_EXAMEN = new Map(MODELE_PAR_DEFAUT.map((s) => [s.cle, s.libelle]));

/** Une liste du sac (multiselect), en chaines. */
export function liste(valeur: unknown): string[] {
  return Array.isArray(valeur) ? valeur.filter((v): v is string => typeof v === 'string') : [];
}

export interface EntreeDuDossier {
  sdk: TakiSdk;
  orgId: string;
  animal: Asset | null;
  /** Repli d'une consultation sans animal : ses seuls soins. */
  consultationId?: string;
  /** Les consultations passees de l'animal, la plus recente d'abord, sans celle en cours. */
  historique: readonly Fiche[];
  catalogue: readonly Product[];
  nomDePersonne: (personId: unknown) => string;
  /** Le jour de reference : celui de la consultation ouverte, ou aujourd'hui. */
  quand: Date;
}

export interface DossierDeLAnimal {
  fiche: Record<string, unknown>;
  proprietaire: Contact | null;
  /** Tous les soins de l'animal. */
  soins: readonly CareRecord[];
  /** Toutes les taches du dossier de l'animal. */
  taches: readonly Todo[];
  tachesOuvertes: readonly Todo[];
  produitsDuCarnet: ReadonlyMap<string, ProduitDuCarnet>;
  carnet: readonly LigneDuCarnet[];
  aProposer: readonly LigneDuCarnet[];
  /** Le rappel le plus proche du carnet. */
  prochain: (LigneDuCarnet & { prochaine: string }) | undefined;
  visites: readonly VisitePassee[];
  lignesDuPasse: readonly LigneDHistorique[];
  poids: readonly PointDePoids[];
  /** La derniere visite avant le jour de reference. */
  derniere: Fiche | undefined;
  erreur: string | null;
  setErreur: (erreur: string | null) => void;
  /** Faux tant que les soins, les taches, les examens et les pesees de l'animal ne sont pas lus. */
  lu: boolean;
  recharger: () => Promise<void>;
  basculerTache: (tache: Todo) => Promise<void>;
  /** Cree une tache du dossier, rattachee a une consultation quand il y en a une. */
  creerTache: (tache: TacheDuSuivi, consultationId: string | null) => Promise<boolean>;
}

export function useDossierDeLAnimal(entree: EntreeDuDossier): DossierDeLAnimal {
  const { sdk, orgId, animal, consultationId, historique, catalogue, nomDePersonne, quand } = entree;
  const assetId = animal?.id ?? '';
  const fiche = sac(animal);
  const [erreur, setErreur] = useState<string | null>(null);

  const [proprietaire, setProprietaire] = useState<Contact | null>(null);
  useEffect(() => {
    setProprietaire(null);
    const ownerId = animal?.ownerContactId;
    if (ownerId === undefined) return;
    let vivant = true;
    void sdk.contacts.get(ownerId).then((c) => vivant && setProprietaire(c), () => undefined);
    return () => {
      vivant = false;
    };
  }, [sdk, animal?.ownerContactId]);

  const [soins, setSoins] = useState<readonly CareRecord[]>([]);
  const [taches, setTaches] = useState<readonly Todo[]>([]);
  /* Lu POUR quel animal : le drapeau ne ment pas l'image qui suit un changement d'animal. */
  const [soinsLusPour, setSoinsLusPour] = useState<string | null>(null);
  const recharger = useCallback(async (): Promise<void> => {
    try {
      const [desSoins, desTaches] = await Promise.all([
        assetId === ''
          ? consultationId === undefined
            ? Promise.resolve([] as CareRecord[])
            : sdk.careRecords.list({ type: 'soin', where: { 'customFields.consultationId': consultationId }, limit: 300 })
          : sdk.careRecords.list({ type: 'soin', where: { assetId }, limit: 300 }),
        assetId === ''
          ? Promise.resolve([] as Todo[])
          : sdk.todos.list({ type: 'dossier', where: { 'customFields.animalId': assetId }, limit: 200 }),
      ]);
      setSoins(desSoins);
      setTaches(desTaches);
    } catch (cause) {
      setErreur(`Dossier illisible : ${raisonDe(cause)}`);
    } finally {
      setSoinsLusPour(assetId);
    }
  }, [sdk, assetId, consultationId]);
  useEffect(() => {
    void recharger();
  }, [recharger]);

  /* L'HISTOIRE CLINIQUE : ses examens en liste (par consultation) et ses pesees. */
  const [examensDeLAnimal, setExamensDeLAnimal] = useState<ReadonlyMap<string, readonly PointExamine[]>>(new Map());
  const [pesees, setPesees] = useState<readonly PointDePoids[]>([]);
  const [histoireLuePour, setHistoireLuePour] = useState<string | null>(null);
  useEffect(() => {
    setExamensDeLAnimal(new Map());
    setPesees([]);
    if (assetId === '') {
      setHistoireLuePour('');
      return;
    }
    let vivant = true;
    void Promise.all([
      sdk.inspections.list({ type: 'examen', where: { assetId }, limit: 200 }),
      sdk.measurements.list({ type: 'constante', where: { assetId, metric: 'weight' }, limit: 200 }),
    ]).then(
      ([examens, mesures]) => {
        if (!vivant) return;
        setExamensDeLAnimal(
          new Map(examens.map((e) => [String(sac(e)['consultationId'] ?? ''), pointsDeLExamen(MODELE_PAR_DEFAUT, e.checkpoints ?? [])])),
        );
        setPesees(mesures.filter((m) => m.archivedAt == null).map((m) => ({ quand: m.measuredAt ?? m.createdAt, kg: m.value })));
        setHistoireLuePour(assetId);
      },
      () => vivant && setHistoireLuePour(assetId),
    );
    return () => {
      vivant = false;
    };
  }, [sdk, assetId]);

  /* LE CARNET DE VACCINATION : les protocoles de l'espece, croises avec les soins par la prevention des produits (`carnet.ts`). */
  const produitsDuCarnet = useMemo(
    () =>
      new Map<string, ProduitDuCarnet>(
        catalogue
          .filter((p) => liste(sac(p)['prevention']).length > 0)
          .map((p) => [p.id, { nom: p.name, couvre: liste(sac(p)['prevention']), especes: liste(sac(p)['species']), delai: sac(p)['reminderDelay'] }]),
      ),
    [catalogue],
  );
  const carnet = useMemo(
    () =>
      lignesDuCarnet(
        fiche['species'],
        soins.map((s) => ({
          id: s.id,
          productId: String(sac(s)['productId'] ?? ''),
          careDate: String(sac(s)['careDate'] ?? ''),
          reminderDate: typeof sac(s)['reminderDate'] === 'string' && sac(s)['reminderDate'] !== '' ? String(sac(s)['reminderDate']) : null,
          lotNumber: typeof sac(s)['lotNumber'] === 'string' ? String(sac(s)['lotNumber']) : '',
        })),
        produitsDuCarnet,
        new Date(),
      ),
    [fiche, soins, produitsDuCarnet],
  );

  const visites: VisitePassee[] = historique.map((c) => ({
    id: String(c['id']),
    quand: instantDe(c) ?? quand,
    motif: valeurEnTexte(sac(c)['reason']),
    diagnostic: valeurEnTexte(sac(c)['diagnosis']),
    therapie: valeurEnTexte(sac(c)['therapy']),
    suivi: valeurEnTexte(sac(c)['followUp']),
    veterinaire: nomDePersonne(sac(c)['veterinarian']),
  }));

  const basculerTache = async (tache: Todo): Promise<void> => {
    try {
      await sdk.todos.update(tache.id, { status: tache.status === 'termine' ? 'aFaire' : 'termine' });
      await recharger();
    } catch (cause) {
      setErreur(`Tâche non modifiée : ${raisonDe(cause)}`);
    }
  };
  const creerTache = async (tache: TacheDuSuivi, rattachee: string | null): Promise<boolean> => {
    try {
      await sdk.todos.create({
        orgId,
        type: 'dossier',
        status: 'aFaire',
        dueAt: tache.date,
        customFields: {
          libelle: tache.libelle,
          ...(tache.assigne === '' ? {} : { assigne: tache.assigne }),
          ...(assetId === '' ? {} : { animalId: assetId }),
          ...(rattachee === null ? {} : { consultationId: rattachee }),
        },
      });
      setErreur(null);
      await recharger();
      return true;
    } catch (cause) {
      setErreur(`Tâche non créée : ${raisonDe(cause)}`);
      return false;
    }
  };

  return {
    fiche,
    proprietaire,
    soins,
    taches,
    tachesOuvertes: taches.filter((t) => t.status !== 'termine'),
    produitsDuCarnet,
    carnet,
    aProposer: aProposerDuCarnet(carnet),
    prochain: carnet
      .filter((l): l is LigneDuCarnet & { prochaine: string } => l.prochaine !== null)
      .sort((a, b) => a.prochaine.localeCompare(b.prochaine))[0],
    visites,
    lignesDuPasse: lignesDHistorique(visites, examensDeLAnimal, SYSTEMES_DE_L_EXAMEN, quand),
    poids: courbeDuPoids(pesees, fiche['weightKg'], quand),
    derniere: historique.find((c) => (instantDe(c)?.getTime() ?? 0) < quand.getTime()),
    erreur,
    setErreur,
    lu: soinsLusPour === assetId && histoireLuePour === assetId,
    recharger,
    basculerTache,
    creerTache,
  };
}
